/**
 * German grading (D-118): the Schreiben and Sprechen parts of the Goethe-Zertifikat sittings of the
 * level chosen at the top. The trainer marks each official criterion of the part with one of its
 * steps (A to E, or full / half / 0 at A1); the E rule (task achievement at 0 → the writing part is
 * worth 0) and the module's scale are applied by the server. Copies flagged for a third rating
 * (the two simulated raters on either side of the pass mark) come first. Automatically marked
 * parts (A1 form, C2 rewrites) are corrected item by item. The AI's marks stay for the calibration.
 */

import { type Production, useAcademyProductionsGradeCreate, useAcademyProductionsList } from "@agencyos/api-client";
import { formatPoints } from "@agencyos/core";
import { Badge, type BadgeTone, Button, EmptyState, GlassCard, GlassSheet, SectionHeader, SelectField, Skeleton, TextAreaField } from "@agencyos/ui-web";
import { ClipboardCheck } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { dateTimeText } from "../billing/shared";
import { useAdminExam } from "./examContext";
import { AiCorrection, GradingFilters, GradingTags, GradingWarnings, useGradingFilters } from "./GradingTools";
import { ErrorList, isObject, num, Pager, str, useAcademyRoles, useRefreshAcademy } from "./shared";

const STATUS_TONE: Record<string, BadgeTone> = { submitted: "default", graded: "success", to_trainer: "danger" };
const MODULE: Record<string, string> = { ee: "Schreiben", eo: "Sprechen" };

interface Criterion {
  key: string;
  points: number[];
  max: number;
  label: string;
  scope: string;
}

function rubricOf(p: Production, language: string): Criterion[] {
  const rows = Array.isArray(p.rubric) ? (p.rubric as unknown[]).filter(isObject) : [];
  return rows.map((c) => {
    const label = isObject(c.label) ? c.label : {};
    return {
      key: str(c.key),
      points: Array.isArray(c.points) ? (c.points as unknown[]).map((v) => num(v)) : [],
      max: num(c.max),
      label: str(language.startsWith("en") ? label.en : label.fr) || str(c.key),
      scope: str(c.scope),
    };
  });
}

function GoetheGradeSheet({ copy, onClose }: { copy: Production; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const grade = useAcademyProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const current = isObject(copy.grade) ? copy.grade : {};
  const rubric = rubricOf(copy, i18n.language);
  const items = Array.isArray(current.items) ? (current.items as unknown[]).filter(isObject) : [];
  const previous = Object.fromEntries((Array.isArray(current.criteria) ? (current.criteria as unknown[]).filter(isObject) : []).map((c) => [str(c.key), String(num(c.points))]));
  const [marks, setMarks] = useState<Record<string, string>>(() =>
    rubric.length ? Object.fromEntries(rubric.map((c) => [c.key, previous[c.key] ?? ""])) : Object.fromEntries(items.map((i) => [str(i.n), String(i.points !== undefined ? num(i.points) : i.correct ? 1 : 0)])),
  );
  const [comment, setComment] = useState(str(current.comment));
  const material = isObject(copy.material) ? copy.material : {};
  const models = isObject(material.models) ? material.models : {};
  const turns = Array.isArray(copy.turns) ? (copy.turns as unknown[]).filter(isObject) : [];
  const keys = rubric.length ? rubric.map((c) => c.key) : items.map((i) => str(i.n));
  const complete = keys.length > 0 && keys.every((k) => marks[k] !== undefined && marks[k] !== "");
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    grade.mutate({ id: copy.id, data: { criteria: keys.map((key) => ({ key, points: Number(marks[key]) })), comment } });
  };
  return (
    <GlassSheet open onClose={onClose} title={`${MODULE[copy.skill] ?? copy.skill} · Teil ${copy.task_number} · ${copy.exam_level}`}>
      <div className="stack">
        <p className="muted">{copy.learner_email}</p>
        <p lang="de">{copy.prompt}</p>
        {copy.text ? (
          <blockquote className="ielts-copy" lang="de">
            {copy.text}
          </blockquote>
        ) : null}
        {turns.length > 0 && (
          <ol className="stack" lang="de">
            {turns
              .filter((turn) => str(turn.text))
              .map((turn, index) => (
                <li key={index}>
                  <strong>{t(`goethe.speaking.who.${turn.role === "learner" ? "learner" : turn.role === "partner" ? "partner" : "examiner"}`)}</strong> {str(turn.text)}
                </li>
              ))}
          </ol>
        )}
        {copy.audio_url ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- its transcript (the turns) is shown above as text
          <audio controls src={copy.audio_url} preload="none" aria-label={t("goethe.review.myRecording")} />
        ) : null}
        {Array.isArray(current.rater_totals) && (current.rater_totals as unknown[]).length === 2 && (
          <p className="muted ao-num">
            {t("goethe.review.raters", { a: formatPoints(num((current.rater_totals as unknown[])[0]), i18n.language), b: formatPoints(num((current.rater_totals as unknown[])[1]), i18n.language) })}
          </p>
        )}
        {str(models.model_answer) && (
          <details>
            <summary>{t("goethe.review.model")}</summary>
            <p lang="de" style={{ whiteSpace: "pre-line" }}>
              {str(models.model_answer)}
            </p>
          </details>
        )}
        {str(models.examiner_notes) && <p className="muted">{str(models.examiner_notes)}</p>}
        <AiCorrection grade={copy.grade} />
        <form className="stack" onSubmit={onSubmit}>
          <fieldset className="stack" disabled={!canEdit}>
            {rubric.length
              ? rubric.map((c) => (
                  <SelectField
                    key={c.key}
                    label={`${c.label}${c.scope === "module" ? ` (${t("goethe.admin.wholeModule")})` : ""}`}
                    value={marks[c.key] ?? ""}
                    onChange={(e) => setMarks((m) => ({ ...m, [c.key]: e.target.value }))}
                    options={[{ value: "", label: "—" }, ...c.points.map((p, index) => ({ value: String(p), label: `${formatPoints(p, i18n.language)} / ${formatPoints(c.max, i18n.language)}${c.points.length === 5 ? ` (${"ABCDE"[index]})` : ""}` }))]}
                  />
                ))
              : items.map((item) => (
                  <SelectField
                    key={str(item.n)}
                    label={`${str(item.n)} · ${str(item.response) || "—"}`}
                    value={marks[str(item.n)] ?? ""}
                    onChange={(e) => setMarks((m) => ({ ...m, [str(item.n)]: e.target.value }))}
                    options={(copy.exam_level === "A1" ? [1, 0] : [2, 1, 0]).map((p) => ({ value: String(p), label: String(p) }))}
                  />
                ))}
            <TextAreaField label={t("academyAdmin.grading.comment")} value={comment} onChange={(e) => setComment(e.target.value)} />
            <ErrorList error={grade.error} />
            <div className="row">
              <Button type="submit" disabled={!complete || grade.isPending}>
                {grade.isPending ? t("common.loading") : t("academyAdmin.grading.grade")}
              </Button>
              {grade.isSuccess ? (
                <span role="status" className="ok">
                  {t("academyAdmin.grading.graded")}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
    </GlassSheet>
  );
}

export function GoetheGradingTab() {
  const { t, i18n } = useTranslation();
  const { params, level } = useAdminExam();
  const grading = useGradingFilters();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Production | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const query = useAcademyProductionsList({ ...params, ...grading.params, page });
  const data = query.data?.status === 200 ? query.data.data : undefined;
  const rows = (data?.results ?? []).filter((p) => p.skill === "ee" || p.skill === "eo");
  const flagged = (p: Production) => isObject(p.grade) && Boolean((p.grade as Record<string, unknown>).third_rating);
  return (
    <GlassCard as="section" className="stack" aria-labelledby="goethe-grading-title">
      <SectionHeader id="goethe-grading-title" title={t("goethe.admin.grading.title", { level })} subtitle={t("goethe.admin.grading.intro")} />
      <GradingWarnings />
      <GradingFilters
          values={grading.values}
          onChange={(next) => {
            grading.setValues(next);
            setPage(1);
          }}
        />
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : rows.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.grading.learner")}</th>
                  <th>{t("academyAdmin.grading.task")}</th>
                  <th>{t("academyAdmin.grading.date")}</th>
                  <th>{t("goethe.admin.grading.points")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {[...rows].sort((a, b) => Number(flagged(b)) - Number(flagged(a))).map((copy) => {
                  const g = isObject(copy.grade) ? (copy.grade as Record<string, unknown>) : {};
                  return (
                    <tr key={copy.id}>
                      <td>
                        <button type="button" className="link" onClick={() => setOpen(copy)}>
                          {copy.learner_email}
                        </button>
                      </td>
                      <td className="ielts-nowrap">
                        {MODULE[copy.skill]} · Teil {copy.task_number} <GradingTags row={copy} />
                      </td>
                      <td className="ielts-nowrap">{dateTimeText(copy.created_at, i18n.language)}</td>
                      <td className="ao-num">
                        {typeof g.points === "number" ? `${formatPoints(g.points, i18n.language)} / ${formatPoints(num(g.max), i18n.language)}` : "—"}
                        {flagged(copy) ? <Badge tone="warning">{t("goethe.admin.grading.third")}</Badge> : null}
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[copy.status] ?? "default"}>{t(`academyAdmin.grading.status.${copy.status}`)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <EmptyState compact icon={<ClipboardCheck size={22} strokeWidth={1.75} />} title={t(`academyAdmin.grading.empty.${grading.values.status}`)} />
      )}
      {open ? <GoetheGradeSheet key={open.id} copy={open} onClose={close} /> : null}
    </GlassCard>
  );
}
